import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Activity, Printer } from 'lucide-react';

/**
 * MedicalAlertChips
 * Renders a compact chip row of the participant's most severe active allergies
 * and active health conditions. Reads from /participants/{id}/medical-alerts-summary
 * (always 200 — even when both flags are off).
 *
 * Rules locked by user:
 * - Max 3 visible per group, then "+N more" link that deep-links to the sub-tab
 * - Severity colours: ANAPHYLAXIS / HIGH = red; SEVERE / MODERATE = amber; MILD / LOW = neutral
 * - Mobile-friendly: chips wrap onto new lines; collapses to single "⚠ N alerts" pill on tiny screens (handled by parent overflow)
 * - Visible to any role with participants.view (incl. Family Portal)
 */

const ALLERGY_TONE = {
  ANAPHYLAXIS: 'bg-red-100 text-red-800 border-red-300',
  SEVERE: 'bg-amber-100 text-amber-800 border-amber-300',
  MODERATE: 'bg-amber-100 text-amber-800 border-amber-300',
  MILD: 'bg-muted text-muted-foreground border-muted',
};

const CONDITION_TONE = {
  HIGH: 'bg-red-100 text-red-800 border-red-300',
  MODERATE: 'bg-amber-100 text-amber-800 border-amber-300',
  LOW: 'bg-muted text-muted-foreground border-muted',
};

export function MedicalAlertChips({ participantId }) {
  const [summary, setSummary] = useState(null);
  const [pdfEnabled, setPdfEnabled] = useState(false);
  const [printing, setPrinting] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/participants/${participantId}/medical-alerts-summary`);
      setSummary(r.data || null);
    } catch (_e) {
      setSummary(null);
    }
    // Independent probe — never blocks summary render
    try {
      const fr = await api.get(`/participants/${participantId}/medical-alert-pdf/feature-flag`);
      setPdfEnabled(!!fr.data?.enabled);
    } catch (_e) { setPdfEnabled(false); }
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = useCallback(async () => {
    setPrinting(true);
    try {
      const r = await api.get(`/participants/${participantId}/medical-alert-pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Open in a new tab so the user can preview + print from the browser PDF viewer
      const w = window.open(url, '_blank');
      if (!w) {
        // Fallback: trigger download
        const a = document.createElement('a');
        a.href = url; a.download = `medical-alert.pdf`; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (_e) {
      // silent — the chip row is safety-critical, don't toast on failure to avoid noise
    } finally { setPrinting(false); }
  }, [participantId]);

  if (!summary) return null;
  const allergiesOn = !!summary?.enabled?.allergies;
  const conditionsOn = !!summary?.enabled?.conditions;

  const allergies = summary.allergies || [];
  const conditions = summary.conditions || [];
  const aTotal = summary.allergies_total_active || 0;
  const cTotal = summary.conditions_total_active || 0;

  const showAllergies = allergiesOn && allergies.length > 0;
  const showConditions = conditionsOn && conditions.length > 0;

  if (!showAllergies && !showConditions) return null;

  const overflowAllergies = aTotal - allergies.length;
  const overflowConditions = cTotal - conditions.length;

  const goAllergies = () => navigate(`/participants/${participantId}?tab=clinical&sub=allergies`);
  const goConditions = () => navigate(`/participants/${participantId}?tab=clinical&sub=health-conditions`);

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 mt-2"
      data-testid="participant-header-medical-chips"
      aria-label="Medical alerts"
    >
      {showAllergies && (
        <>
          <span className="inline-flex items-center text-[10px] uppercase tracking-wide text-muted-foreground mr-0.5">
            <AlertTriangle className="h-3 w-3 mr-0.5 text-red-600" />Allergy
          </span>
          {allergies.map(a => (
            <button
              key={a.id}
              type="button"
              onClick={goAllergies}
              data-testid={`medical-chip-allergy-${a.id}`}
              className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${ALLERGY_TONE[a.severity] || 'bg-muted text-muted-foreground border-muted'} hover:brightness-95 transition`}
              title={`${a.allergen} — ${a.severity}`}
            >
              <span className="font-medium">{a.allergen}</span>
              <span className="ml-1 opacity-80 text-[9px] uppercase">{a.severity}</span>
            </button>
          ))}
          {overflowAllergies > 0 && (
            <button
              type="button"
              onClick={goAllergies}
              data-testid="medical-chip-allergies-overflow"
              className="text-[10px] px-2 py-0.5 rounded-full border bg-muted text-muted-foreground hover:bg-muted/80"
            >
              +{overflowAllergies} more
            </button>
          )}
        </>
      )}

      {showAllergies && showConditions && (
        <span className="text-muted-foreground/40 mx-1" aria-hidden="true">|</span>
      )}

      {showConditions && (
        <>
          <span className="inline-flex items-center text-[10px] uppercase tracking-wide text-muted-foreground mr-0.5">
            <Activity className="h-3 w-3 mr-0.5 text-blue-600" />Active
          </span>
          {conditions.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={goConditions}
              data-testid={`medical-chip-condition-${c.id}`}
              className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${CONDITION_TONE[c.severity] || 'bg-muted text-muted-foreground border-muted'} hover:brightness-95 transition`}
              title={`${c.condition_name} — ${c.severity}`}
            >
              <span className="font-medium">{c.condition_name}</span>
            </button>
          ))}
          {overflowConditions > 0 && (
            <button
              type="button"
              onClick={goConditions}
              data-testid="medical-chip-conditions-overflow"
              className="text-[10px] px-2 py-0.5 rounded-full border bg-muted text-muted-foreground hover:bg-muted/80"
            >
              +{overflowConditions} more
            </button>
          )}
        </>
      )}

      {/* Phase 3B — Print Medical Alert PDF (feature-flag gated, only when there's something worth printing) */}
      {pdfEnabled && (showAllergies || showConditions) && (
        <button
          type="button"
          onClick={downloadPdf}
          disabled={printing}
          data-testid="medical-alert-print-pdf"
          aria-label="Print medical alert"
          title="Print emergency medical alert (PDF)"
          className="ml-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-white text-red-700 border-red-300 hover:bg-red-50 transition"
        >
          <Printer className="h-3 w-3" />
          {printing ? 'Generating…' : 'Print Alert'}
        </button>
      )}
    </div>
  );
}

export default MedicalAlertChips;
